import { useMutation } from '@tanstack/react-query';
import { useReportsApi } from '../../Hooks/useReportsApi';
import { useSnackbar } from '../stateless/SnackBarProvider';
import {
  SalesReportRequest,
  CancelledOrdersReportRequest,
  OrderSummaryReportRequest,
  AdminCommissionReportRequest,
  RejectedOrdersReportRequest,
  PendingOrdersReportRequest,
  MerchantPayoutReportRequest,
  RefundReportRequest,
} from '../../client-sdk/api';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const createDownloadMutationConfig = <TRequest,>(
  mutationKey: string[],
  filenamePrefix: string,
  downloadFn: (request: TRequest) => Promise<Blob>,
  successMessage: string,
  errorMessage: string,
  showMessage: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void,
  t: (key: string) => string,
  noDataMessage?: string
) => {
  return {
    mutationKey,
    mutationFn: (request: TRequest) => downloadFn(request),
    onSuccess: (blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filenamePrefix}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showMessage(t(successMessage), 'success');
    },
    onError: (error: { response?: { data?: { message?: string }; status?: number } }) => {
      const errorMsg = error?.response?.data?.message || t(errorMessage);
      const statusCode = error?.response?.status;
      
      // Check if it's a "no data found" type error (404 with specific messages)
      const isNoDataError = 
        statusCode === 404 || 
        (typeof errorMsg === 'string' && (
          errorMsg.toLowerCase().includes('no data') ||
          errorMsg.toLowerCase().includes('not found') ||
          errorMsg.toLowerCase().includes('no matching') ||
          errorMsg.toLowerCase().includes('no pending') ||
          errorMsg.toLowerCase().includes('no cancelled') ||
          errorMsg.toLowerCase().includes('no rejected') ||
          errorMsg.toLowerCase().includes('no orders') ||
          errorMsg.toLowerCase().includes('no commission')
        ));

      if (isNoDataError && noDataMessage) {
        // Show as info message for "no data" scenarios
        showMessage(t(noDataMessage) || errorMsg, 'info');
      } else {
        // Show as error for actual errors
        showMessage(errorMsg, 'error');
      }
    },
  };
};

export const useDownloadSalesReport = () => {
  const { downloadSalesReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<SalesReportRequest>(
      ['downloadSalesReport'],
      'sales_report',
      downloadSalesReport,
      'reports.sales.downloadSuccess',
      'reports.sales.downloadError',
      showMessage,
      t
    )
  );
};

export const useDownloadOrderSummaryReport = () => {
  const { downloadOrderSummaryReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<OrderSummaryReportRequest>(
      ['downloadOrderSummaryReport'],
      'order_summary_report',
      downloadOrderSummaryReport,
      'reports.orderSummary.downloadSuccess',
      'reports.orderSummary.downloadError',
      showMessage,
      t
    )
  );
};

export const useDownloadAdminCommissionReport = () => {
  const { downloadAdminCommissionReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<AdminCommissionReportRequest>(
      ['downloadAdminCommissionReport'],
      'admin_commission_report',
      downloadAdminCommissionReport,
      'reports.adminCommission.downloadSuccess',
      'reports.adminCommission.downloadError',
      showMessage,
      t,
      'reports.adminCommission.noData'
    )
  );
};

export const useDownloadCancelledOrdersReport = () => {
  const { downloadCancelledOrdersReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<CancelledOrdersReportRequest>(
      ['downloadCancelledOrdersReport'],
      'cancelled_orders_report',
      downloadCancelledOrdersReport,
      'reports.cancelledOrders.downloadSuccess',
      'reports.cancelledOrders.downloadError',
      showMessage,
      t,
      'reports.cancelledOrders.noData'
    )
  );
};

export const useDownloadRejectedOrdersReport = () => {
  const { downloadRejectedOrdersReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<RejectedOrdersReportRequest>(
      ['downloadRejectedOrdersReport'],
      'rejected_orders_report',
      downloadRejectedOrdersReport,
      'reports.rejectedOrders.downloadSuccess',
      'reports.rejectedOrders.downloadError',
      showMessage,
      t,
      'reports.rejectedOrders.noData'
    )
  );
};

export const useDownloadPendingOrdersReport = () => {
  const { downloadPendingOrdersReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<PendingOrdersReportRequest>(
      ['downloadPendingOrdersReport'],
      'pending_orders_report',
      downloadPendingOrdersReport,
      'reports.pendingOrders.downloadSuccess',
      'reports.pendingOrders.downloadError',
      showMessage,
      t,
      'reports.pendingOrders.noData'
    )
  );
};

export const useDownloadMerchantPayoutReport = () => {
  const { downloadMerchantPayoutReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<MerchantPayoutReportRequest>(
      ['downloadMerchantPayoutReport'],
      'merchant_payout_report',
      downloadMerchantPayoutReport,
      'reports.merchantPayout.downloadSuccess',
      'reports.merchantPayout.downloadError',
      showMessage,
      t
    )
  );
};

export const useDownloadRefundReport = () => {
  const { downloadRefundReport } = useReportsApi();
  const { showMessage } = useSnackbar();
  const { t } = useTranslation();
  return useMutation(
    createDownloadMutationConfig<RefundReportRequest>(
      ['downloadRefundReport'],
      'refund_report',
      downloadRefundReport,
      'reports.refund.downloadSuccess',
      'reports.refund.downloadError',
      showMessage,
      t
    )
  );
};

